import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBlogPosts } from '@/hooks/useBlogPosts';

interface BlogGridProps {
  searchQuery: string;
}

const POSTS_PER_PAGE = 9;

export default function BlogGrid({ searchQuery }: BlogGridProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const { t } = useTranslation();
  const { posts, loading, error, refresh } = useBlogPosts();

  const filteredPosts = useMemo(() => {
    let result = posts;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q),
      );
    }
    return result;
  }, [posts, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE));
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const pagePosts = filteredPosts.slice(startIndex, startIndex + POSTS_PER_PAGE);

  // Reset to page 1 when search changes
  useMemo(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  if (loading) {
    return (
      <section className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-6 pb-10 lg:pb-[100px]">
        <div className="flex flex-col items-center justify-center gap-3 py-24">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
          <p className="text-sm text-secondary-500">{t('blog.loading')}</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-6 pb-10 lg:pb-[100px]">
        <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
          <i className="ri-error-warning-line text-3xl text-red-500" />
          <p className="text-sm text-secondary-600">{t('blog.loadFailed')}</p>
          <p className="text-xs text-secondary-400">{error}</p>
          <button
            type="button"
            onClick={refresh}
            className="mt-2 inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-[13px] font-bold text-white transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            <i className="ri-refresh-line text-sm" />
            {t('blog.tryAgain')}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-6 pb-10 lg:pb-[100px]">
      <div className="flex flex-col gap-6">
        {/* Section label */}
        <div className="flex items-center gap-2.5 text-[12px] font-bold uppercase tracking-[0.1em] text-secondary-400 mt-6">
          <span>{t('blog.latest')}</span>
          <span className="h-px flex-1 bg-secondary-200" />
        </div>

        {/* Blog grid */}
        {pagePosts.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-secondary-500 text-sm">{t('blog.noGuides')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {pagePosts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group flex h-full w-full flex-col overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-[0_4px_14px_-6px_rgba(15,23,42,0.08)] transition-all duration-200 hover:-translate-y-1 hover:border-primary-200 hover:shadow-[0_18px_36px_-18px_rgba(15,118,110,0.28)]"
              >
                <div className="relative aspect-[16/10] w-full overflow-hidden">
                  <img
                    alt={post.title}
                    src={post.image}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                  />
                  {post.category !== 'All topics' && (
                    <span className="absolute left-3 top-3 z-[2] inline-flex items-center rounded-xl bg-white/95 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.04em] text-primary-700 shadow-[0_2px_8px_rgba(0,0,0,0.1)] backdrop-blur-sm">
                      {post.category}
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="line-clamp-2 text-[15px] font-bold leading-[1.34] tracking-[-0.01em] text-secondary-900 transition-colors group-hover:text-primary-600">
                    {post.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-[12.5px] leading-[1.55] text-secondary-600">
                    {post.excerpt}
                  </p>
                  <div className="mt-auto flex items-center gap-2.5 pt-3.5 text-[11.5px] text-secondary-400">
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-calendar-line text-xs" />
                      {post.date}
                    </span>
                    <span className="size-[3px] rounded-full bg-secondary-300" />
                    <span>{post.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex justify-center lg:mt-6">
            <nav aria-label={t('blog.pagination')} className="flex items-center gap-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  aria-label={t('blog.goToPage', { page })}
                  aria-current={page === currentPage ? 'page' : undefined}
                  onClick={() => setCurrentPage(page)}
                  className={`flex h-10 w-10 items-center justify-center rounded-[12px] text-[12px] font-bold leading-[1.2] transition-colors ${
                    page === currentPage
                      ? 'bg-primary-500 text-white'
                      : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
                  }`}
                >
                  {page}
                </button>
              ))}
            </nav>
          </div>
        )}
      </div>
    </section>
  );
}